import React, { useState } from 'react';

const CAMPAIGN_INSTRUCTIONS_TEMPLATE = `Campaign Objective:
- Book a 15-minute discovery call with qualified leads.
- End goal: customer shares preferred day/time for call.

Role:
- You are a sales assistant for a marketing agency.
- You qualify inbound leads only.
- Do not discuss unrelated support topics unless user asks.

Tone and Communication Style:
- Match customer language (Arabic or English).
- Keep replies short (1-3 lines).
- Friendly, professional, direct.

Discovery Questions (ask one by one):
1) What is your business type and main offer?
2) What is your #1 goal in the next 90 days?
3) Are you currently running ads?
4) How do you measure results now? (ROAS/CPL/CPA/sales)
5) What is your monthly budget range?
6) Are you the decision maker?
7) When do you want to start?

Flow Rules:
- Ask one question at a time.
- Acknowledge the answer briefly, then ask next missing question.
- If answer is vague, ask one clarification only.
- Never repeat already collected data unless customer asks.

End Goal:
- After collecting required data, send a short summary.
- Ask for a 15-minute call in the next 3 days.`;

const FOLLOW_UP_TEMPLATE = `Follow-up Triggers:
- Start follow-up after 2 hours of no reply.

Follow-up Sequence:
- Attempt 1 (after 2h):
  "Quick follow-up 🙏 Do you want to continue so we can prepare the right plan for you?"
- Attempt 2 (after 24h):
  "I can help you with a clear plan based on your goal. If suitable, we can finish in 2 minutes."
- Attempt 3 (after 72h):
  "Final follow-up from me. If you want to continue later, reply with (start)."

Max Attempts:
- Maximum 3 follow-ups, then stop.

Escalation Rules:
- If customer replies: resume qualification immediately.
- If customer says not interested: close politely and mark cold lead.
- If customer requests human help: handoff to human sales.`;

const TRELLO_TEMPLATE_JSON = `{
  "version": 1,
  "sectionTitle": "Marketing Lead Summary",
  "style": "concise",
  "fieldOrder": [
    "user_name",
    "lead_goal",
    "marketing_experience",
    "measurement_method",
    "urgency",
    "objections",
    "budget_range",
    "decision_maker",
    "service_interest",
    "next_action",
    "lead_label"
  ],
  "labels": {
    "user_name": "Client Name",
    "lead_goal": "Business Goal (90 Days)",
    "marketing_experience": "Current/Previous Marketing",
    "measurement_method": "Measurement Method",
    "urgency": "Start Timeline",
    "objections": "Objections",
    "budget_range": "Budget Range",
    "decision_maker": "Decision Maker",
    "service_interest": "Service Interest",
    "next_action": "Next Sales Step",
    "lead_label": "Lead Stage"
  },
  "requiredFields": [
    "user_name",
    "lead_goal",
    "budget_range",
    "next_action",
    "lead_label"
  ],
  "fallbackText": "Unknown"
}`;

const COPY_RESET_MS = 2200;

interface CopySectionProps {
  id: string;
  title: string;
  description: string;
  text: string;
  copiedKey: string | null;
  onCopy: (id: string, text: string) => Promise<void>;
}

const CopySection: React.FC<CopySectionProps> = ({
  id,
  title,
  description,
  text,
  copiedKey,
  onCopy,
}) => {
  const copied = copiedKey === id;
  return (
    <article className="blueprint-card">
      <header className="blueprint-card-head">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => onCopy(id, text)}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </header>
      <pre className="blueprint-code">
        <code>{text}</code>
      </pre>
    </article>
  );
};

export const CampaignBlueprintPage: React.FC = () => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copyToClipboard = async (key: string, value: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(null), COPY_RESET_MS);
    } catch (error) {
      console.error('Clipboard copy failed:', error);
      setCopiedKey(null);
    }
  };

  return (
    <div className="page-section">
      <section className="panel">
        <p className="eyebrow">Manual</p>
        <h2>Campaign Blueprint (Easy English)</h2>
        <p className="blueprint-intro">
          Use this page as a copy-ready manual for high quality campaigns.
          Keep instructions short, clear, and deterministic so AI behavior is stable.
        </p>
        <div className="blueprint-tips">
          <div>
            <strong>Best UX Rule</strong>
            <p>Ask one question per message. Never send long paragraphs.</p>
          </div>
          <div>
            <strong>Best Performance Rule</strong>
            <p>Keep prompts structured and Trello template JSON deterministic.</p>
          </div>
          <div>
            <strong>Best Reliability Rule</strong>
            <p>Use required fields + fallback text so summaries never break.</p>
          </div>
        </div>
      </section>

      <section className="panel">
        <h3>Step-by-Step Setup</h3>
        <ol className="blueprint-steps">
          <li>
            Set a clear <strong>Campaign Name</strong> (example: <code>MARKETING_SALES_QUALIFICATION</code>).
          </li>
          <li>
            Add 1-5 short <strong>Trigger Keywords</strong> (example: <code>.market</code>, <code>marketing</code>, <code>ads</code>).
          </li>
          <li>
            Paste the campaign instructions template and customize only business-specific parts.
          </li>
          <li>
            Paste follow-up policy with exact delays and max attempts.
          </li>
          <li>
            Paste Trello JSON template and keep keys stable across updates.
          </li>
          <li>
            Test with a real chat flow and verify: questions, summary, Trello movement, and labels.
          </li>
        </ol>
      </section>

      <CopySection
        id="campaign-instructions"
        title="1) Campaign Instructions Template"
        description="Copy this into Campaign Instructions, then edit goals/questions for your service."
        text={CAMPAIGN_INSTRUCTIONS_TEMPLATE}
        copiedKey={copiedKey}
        onCopy={copyToClipboard}
      />

      <CopySection
        id="follow-up"
        title="2) Follow-up Instructions Template"
        description="Copy this into Campaign Follow-up Instructions."
        text={FOLLOW_UP_TEMPLATE}
        copiedKey={copiedKey}
        onCopy={copyToClipboard}
      />

      <CopySection
        id="trello-json"
        title="3) Trello Template JSON"
        description="Copy this into Campaign Trello Template (JSON only)."
        text={TRELLO_TEMPLATE_JSON}
        copiedKey={copiedKey}
        onCopy={copyToClipboard}
      />

      <section className="panel">
        <h3>Performance and UX Checklist</h3>
        <ul className="blueprint-list">
          <li>Keep replies short (1-3 lines max).</li>
          <li>Do not ask multiple qualification questions in one message.</li>
          <li>Do not repeat already collected answers.</li>
          <li>Use required Trello fields for stable summaries.</li>
          <li>Use simple trigger keywords that users actually type.</li>
          <li>Keep follow-up attempts limited and time-based.</li>
          <li>Use deterministic JSON; avoid changing field names frequently.</li>
        </ul>
      </section>

      <section className="panel">
        <h3>Quick QA Test Script</h3>
        <ul className="blueprint-list">
          <li>Send trigger keyword and confirm campaign starts.</li>
          <li>Answer all qualification questions in order.</li>
          <li>Check AI asks for call scheduling after required fields are complete.</li>
          <li>Check Trello description includes AI summary block with expected fields.</li>
          <li>Check Trello card moved to the correct list after stage change.</li>
          <li>Check label exists on card (Hot/Warm/Cold).</li>
        </ul>
      </section>
    </div>
  );
};
